import React from 'react';
import { Plus } from 'lucide-react';
import {
  calcAggregateVariationPct,
  calcCompetitorLineSubtotal,
  calcOfferTotals,
  calcPriceVariationPct,
  formatVariationPct,
} from '../placeholders/quotationLossCompetitorUtils';

const QuotationCloseWizard = ({
  ui,
  quotation,
  lineItems,
  competitors,
  isAdjudicadaTransitional,
  closeStatus,
  closeWizardStep,
  closingDate,
  setClosingDate,
  lossReason,
  setLossReason,
  lossReasonOptions,
  lossBuyerComment,
  setLossBuyerComment,
  competitorSelection,
  setCompetitorSelection,
  showNewCompetitor,
  setShowNewCompetitor,
  newCompetitorName,
  setNewCompetitorName,
  competitorAmount,
  setCompetitorAmount,
  lossCompetitorLines,
  updateLossCompetitorLine,
  onSaveNewCompetitor,
  closeError,
  isClosing,
  savingExtra,
  onCancel,
  onBack,
  onNext,
  onSkipRequest,
  onConfirm,
  formatMoney,
  inputStyle,
  btnBase,
  renderNonPerdidaFields,
  renderVendidaFields,
  vendidaWizardStep = 1,
  maxClosingDate = '',
}) => {
  const isPerdida = closeStatus === 'Perdida';
  const isVendida = closeStatus === 'Vendida';
  const showSkipRequest = isVendida && (vendidaWizardStep === 2 || vendidaWizardStep === 3);
  const aggregateVariation = calcAggregateVariationPct(lineItems, lossCompetitorLines);
  const { ourTotal: ourOfferTotal, competitorTotal: competitorOfferTotal } = calcOfferTotals(
    lineItems,
    lossCompetitorLines
  );
  const selectedStatusStyle = {
    Adjudicada: { bg: '#dcfce7', text: '#166534' },
    Vendida: { bg: '#ccfbf1', text: '#0f766e' },
    Perdida: { bg: '#fee2e2', text: '#991b1b' },
    Rechazada: { bg: '#fce7f3', text: '#9d174d' },
    Cancelada: { bg: '#f3f4f6', text: '#374151' },
    Desierta: { bg: '#ffedd5', text: '#9a3412' },
  }[closeStatus] || { bg: '#f3f4f6', text: '#374151' };

  const textareaStyle = {
    ...inputStyle,
    height: 'auto',
    minHeight: '72px',
    padding: '10px',
    resize: 'vertical',
    lineHeight: 1.45,
  };

  const fieldLabel = (text) => (
    <span style={{ fontSize: '12px', fontWeight: 600, color: ui.muted }}>{text}</span>
  );

  const renderPerdidaStep1 = () => (
    <>
      <div style={{ fontSize: '12px', fontWeight: 700, color: ui.muted, marginBottom: '4px' }}>
        Paso 1 de 2 — Información general
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {fieldLabel('Fecha de cierre')}
          <input
            type="date"
            value={closingDate}
            max={maxClosingDate || undefined}
            onChange={(e) => setClosingDate(e.target.value)}
            disabled={isClosing}
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {fieldLabel('Motivo de pérdida')}
          <select
            value={lossReason}
            onChange={(e) => setLossReason(e.target.value)}
            disabled={isClosing}
            style={inputStyle}
          >
            <option value="">Seleccionar motivo…</option>
            {lossReasonOptions.map((opt) => (
              <option key={opt.code || opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', gridColumn: '1 / -1' }}>
          {fieldLabel('Comentario del comprador')}
          <textarea
            value={lossBuyerComment}
            onChange={(e) => setLossBuyerComment(e.target.value)}
            disabled={isClosing}
            placeholder="Comentarios o feedback del comprador sobre la decisión"
            style={textareaStyle}
          />
        </label>
      </div>

      <div style={{ fontSize: '12px', fontWeight: 700, color: ui.text, marginTop: '2px' }}>
        Competencia (opcional)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
          {fieldLabel('Empresa competidora')}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <select
              value={competitorSelection}
              onChange={(e) => {
                setCompetitorSelection(e.target.value);
                setShowNewCompetitor(false);
                setNewCompetitorName('');
              }}
              disabled={isClosing || showNewCompetitor}
              style={{ ...inputStyle, flex: 1, minWidth: 0 }}
            >
              <option value="">Seleccionar empresa…</option>
              {(competitors || []).map((entry) => (
                <option key={entry.id} value={String(entry.id)}>
                  {entry.name}
                </option>
              ))}
            </select>
            {onSaveNewCompetitor ? (
              <button
                type="button"
                onClick={() => {
                  setShowNewCompetitor((prev) => !prev);
                  if (!showNewCompetitor) {
                    setCompetitorSelection('');
                  }
                }}
                disabled={isClosing || savingExtra}
                title="Nueva empresa competidora"
                aria-label="Nueva empresa competidora"
                style={{
                  flexShrink: 0,
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  border: `1px solid ${showNewCompetitor ? ui.interactive : ui.inputBorder}`,
                  background: showNewCompetitor ? ui.interactive : ui.inputBg,
                  color: showNewCompetitor ? '#fff' : ui.text,
                  cursor: isClosing || savingExtra ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  opacity: isClosing || savingExtra ? 0.75 : 1,
                }}
              >
                <Plus size={16} />
              </button>
            ) : null}
          </div>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {fieldLabel('Monto adjudicado por la competencia')}
          <input
            type="number"
            min="0"
            step="1"
            value={competitorAmount}
            onChange={(e) => setCompetitorAmount(e.target.value)}
            disabled={isClosing}
            placeholder="Monto total neto de la oferta"
            style={inputStyle}
          />
        </label>
      </div>
      {showNewCompetitor ? (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
            {fieldLabel('Nombre de la nueva empresa')}
            <input
              type="text"
              value={newCompetitorName}
              onChange={(e) => setNewCompetitorName(e.target.value)}
              disabled={isClosing || savingExtra}
              placeholder="Nombre de la empresa"
              style={inputStyle}
            />
          </label>
          {onSaveNewCompetitor ? (
            <button
              type="button"
              disabled={isClosing || savingExtra || !String(newCompetitorName || '').trim()}
              onClick={onSaveNewCompetitor}
              style={{ ...btnBase, border: 'none', background: '#2563eb', color: '#fff' }}
            >
              {savingExtra ? 'Guardando…' : 'Guardar empresa'}
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );

  const renderPerdidaStep2 = () => (
    <>
      <div style={{ fontSize: '12px', fontWeight: 700, color: ui.muted, marginBottom: '4px' }}>
        Paso 2 de 2 — Detalle por línea
      </div>
      <div style={{ fontSize: '12px', color: ui.muted, lineHeight: 1.45, marginBottom: '6px' }}>
        Indica el producto y precio unitario neto ofertado por la competencia en cada línea.
      </div>
      {lineItems.length === 0 ? (
        <div style={{ fontSize: '12px', color: ui.muted }}>Esta cotización no tiene ítems.</div>
      ) : (
        <div style={{ overflowX: 'auto', border: `1px solid ${ui.border}`, borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '940px' }}>
            <thead>
              <tr style={{ background: ui.header }}>
                {[
                  'Nuestro producto',
                  'Nuestro precio unit. neto',
                  'Producto competencia',
                  'Precio unit. neto competencia',
                  'Subtotal neto competencia',
                  'Variación %',
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '8px 10px',
                      textAlign: h === 'Nuestro producto' || h === 'Producto competencia' ? 'left' : 'right',
                      fontSize: '11px',
                      color: ui.muted,
                      borderBottom: `1px solid ${ui.border}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item) => {
                const itemId = Number(item.id);
                const row = lossCompetitorLines[itemId] || {};
                const qty = Number(item.quantity || 0);
                const ourUnit = Number(item.unit_price || 0);
                const competitorUnit = Number(row.unitPrice || 0);
                const subtotal = calcCompetitorLineSubtotal(qty, competitorUnit);
                const variation = calcPriceVariationPct(ourUnit, competitorUnit);
                return (
                  <tr key={item.id || `${item.item_code}-${item.item_name}`}>
                    <td style={{ padding: '8px 10px', fontSize: '12px', color: ui.text, borderBottom: `1px solid ${ui.border}` }}>
                      <div style={{ fontWeight: 600 }}>{item.item_name || item.product_name || '—'}</div>
                      <div style={{ fontSize: '11px', color: ui.muted }}>
                        Cant. {qty.toLocaleString('es-CL')}
                      </div>
                    </td>
                    <td style={{ padding: '8px 10px', fontSize: '12px', textAlign: 'right', borderBottom: `1px solid ${ui.border}`, whiteSpace: 'nowrap' }}>
                      {ourUnit > 0 ? formatMoney(ourUnit, quotation.currency) : '—'}
                    </td>
                    <td style={{ padding: '8px 10px', borderBottom: `1px solid ${ui.border}` }}>
                      <input
                        type="text"
                        value={row.product || ''}
                        onChange={(e) => updateLossCompetitorLine(itemId, 'product', e.target.value)}
                        disabled={isClosing}
                        placeholder="Producto ofertado"
                        style={{ ...inputStyle, width: '100%' }}
                      />
                    </td>
                    <td style={{ padding: '8px 10px', borderBottom: `1px solid ${ui.border}` }}>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={row.unitPrice ?? ''}
                        onChange={(e) => updateLossCompetitorLine(itemId, 'unitPrice', e.target.value)}
                        disabled={isClosing}
                        placeholder="Precio neto"
                        style={{ ...inputStyle, width: '100%', textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ padding: '8px 10px', fontSize: '12px', textAlign: 'right', borderBottom: `1px solid ${ui.border}`, whiteSpace: 'nowrap' }}>
                      {competitorUnit > 0 ? formatMoney(subtotal, quotation.currency) : '—'}
                    </td>
                    <td style={{ padding: '8px 10px', fontSize: '12px', fontWeight: 700, textAlign: 'right', borderBottom: `1px solid ${ui.border}`, whiteSpace: 'nowrap' }}>
                      {formatVariationPct(variation)}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ background: ui.sectionBg }}>
                <td style={{ padding: '10px', fontSize: '12px', fontWeight: 700, color: ui.text, borderTop: `1px solid ${ui.border}` }}>
                  Totales ofertados
                </td>
                <td style={{ padding: '10px', fontSize: '12px', fontWeight: 700, textAlign: 'right', borderTop: `1px solid ${ui.border}`, whiteSpace: 'nowrap' }}>
                  <div style={{ fontSize: '10px', color: ui.muted, fontWeight: 600 }}>Nosotros</div>
                  {formatMoney(ourOfferTotal, quotation.currency)}
                </td>
                <td style={{ padding: '10px', borderTop: `1px solid ${ui.border}` }} />
                <td style={{ padding: '10px', borderTop: `1px solid ${ui.border}` }} />
                <td style={{ padding: '10px', fontSize: '12px', fontWeight: 700, textAlign: 'right', borderTop: `1px solid ${ui.border}`, whiteSpace: 'nowrap' }}>
                  <div style={{ fontSize: '10px', color: ui.muted, fontWeight: 600 }}>Competencia</div>
                  {competitorOfferTotal > 0 ? formatMoney(competitorOfferTotal, quotation.currency) : '—'}
                </td>
                <td style={{ padding: '10px', fontSize: '13px', fontWeight: 800, textAlign: 'right', borderTop: `1px solid ${ui.border}`, color: ui.text, whiteSpace: 'nowrap' }}>
                  {formatVariationPct(aggregateVariation)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  const showBack = true;
  const showBackToStatusPicker =
    (isPerdida && closeWizardStep === 1)
    || (isVendida && vendidaWizardStep === 1)
    || (!isPerdida && !isVendida);
  const showNext =
    (isPerdida && closeWizardStep === 1)
    || (isVendida && vendidaWizardStep < 3);
  const showConfirm =
    (!isPerdida && !isVendida)
    || (isPerdida && closeWizardStep === 2)
    || (isVendida && vendidaWizardStep === 3);

  return (
    <div
      style={{
        border: `1px solid ${ui.border}`,
        borderRadius: '10px',
        padding: '14px',
        background: ui.panel,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '13px', fontWeight: 750, color: ui.text }}>
          Actualizar estado
        </div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 10px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 700,
            background: selectedStatusStyle.bg,
            color: selectedStatusStyle.text,
            whiteSpace: 'nowrap',
          }}
        >
          {closeStatus}
        </span>
      </div>
      {isAdjudicadaTransitional ? (
        <div style={{ fontSize: '12px', color: ui.muted, lineHeight: 1.45 }}>
          Completa la información para registrar el estado final de la cotización.
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 }}>
          {isPerdida
            ? (closeWizardStep === 1 ? renderPerdidaStep1() : renderPerdidaStep2())
            : isVendida
              ? renderVendidaFields?.()
              : renderNonPerdidaFields?.()}
          {closeError ? (
            <div style={{ color: '#b91c1c', fontSize: '12px', fontWeight: 600 }}>{closeError}</div>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
            <button
              type="button"
              disabled={isClosing}
              onClick={onCancel}
              style={{
                ...btnBase,
                border: `1px solid ${ui.border}`,
                background: ui.panel,
                color: ui.text,
              }}
            >
              Cancelar
            </button>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {showBack ? (
                <button
                  type="button"
                  disabled={isClosing}
                  onClick={onBack}
                  style={{
                    ...btnBase,
                    border: `1px solid ${ui.border}`,
                    background: ui.panel,
                    color: ui.text,
                  }}
                >
                  {showBackToStatusPicker ? 'Cambiar estado' : 'Atrás'}
                </button>
              ) : null}
              {showSkipRequest ? (
                <button
                  type="button"
                  disabled={isClosing}
                  onClick={onSkipRequest}
                  style={{
                    ...btnBase,
                    border: `1px solid ${ui.border}`,
                    background: ui.panel,
                    color: ui.text,
                  }}
                >
                  Omitir solicitud
                </button>
              ) : null}
              {showNext ? (
                <button
                  type="button"
                  disabled={isClosing}
                  onClick={onNext}
                  style={{
                    ...btnBase,
                    border: 'none',
                    background: ui.interactive,
                    color: '#fff',
                  }}
                >
                  Siguiente
                </button>
              ) : null}
              {showConfirm ? (
                <button
                  type="button"
                  disabled={isClosing}
                  onClick={onConfirm}
                  style={{
                    ...btnBase,
                    border: 'none',
                    background: closeStatus === 'Vendida' ? '#0f766e' : closeStatus === 'Adjudicada' ? '#16a34a' : ui.interactive,
                    color: '#fff',
                  }}
                >
                  {isClosing ? 'Guardando…' : 'Confirmar cierre'}
                </button>
              ) : null}
            </div>
          </div>
      </div>
    </div>
  );
};

export default QuotationCloseWizard;
